import {
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-enterprise";
import { MenuItem } from "@openng/optimus-ui/api";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from "src/modules/utils/shared/utils";
import { ObjectActionWithObject } from "../../models/object-action-with-object.model";
import { ObjectActionRegistry } from "../../models/object-action-registry.model";
import { TableCellButton } from "../../models/table-cell-button.model";
import { TableCellEvent } from "../../models/table-cell-event.model";
import { TableCell } from "../../models/table-cell.model";
import { TableRow } from "../../models/table-row.model";
import { TableCellType } from "../../netgen/TableCellType";
import { TableColumn } from "../../netgen/TableColumn";
import { SmTableComponent } from "../sm-table/sm-table.component";
import { ToolTipDefinition } from "../../models/ToolTipDefinition.model";

@Component({
    selector: "sm-table-cell",
    templateUrl: "./sm-table-cell.component.html",
    styleUrl: "./sm-table-cell.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SmTableCellComponent implements ICellRendererAngularComp {
    DEFAULT_TIPPY_PROPS = {
        placement: "auto",
        strategy: "fixed",
        popperOptions: {
            modifiers: [
                {
                    name: "preventOverflow",
                    options: {
                        boundary: "viewport",
                        padding: 8,
                    },
                },
                {
                    name: "flip",
                    options: {
                        fallbackPlacements: ["top", "bottom", "right", "left"],
                    },
                },
            ],
        },
    };

    _Utils = Utils;
    _TableCellType = TableCellType;

    app = inject(MainAppService);

    column: TableColumn;
    parentTable: SmTableComponent;
    params: ICellRendererParams;
    tableRow: TableRow;
    displayValue: string;
    mouseInside = false;
    tableCell: TableCell;
    toolTip: ToolTipDefinition;
    buttonActions: ObjectActionWithObject[] = [];
    private truncationTooltipActive = false;

    @ViewChild("value", { read: ElementRef })
    value: ElementRef<HTMLElement>;

    //Damit Buttons vertikal zentriert angezeigt werden
    @HostBinding("class.height100p")
    get height100pClass(): boolean {
        return this.column?.cellType == TableCellType.button;
    }

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.column = this.params.colDef.context.tableColumn;
        this.parentTable = this.params.colDef.context.table;
        this.tableRow = this.params.data as TableRow;
        this.tableCell = Objects.castOrNull(this.tableRow.values[this.column.id], TableCell);
        this.buttonActions = this.getObjectActionsFromProviders();
        this.displayValue = this.params.valueFormatted;
        this.toolTip = this.tableCell?.toolTip ?? this.tableRow.toolTip ?? this.parentTable.getCellSemanticObjectToolTip(this.params);

        if (this.column.cellRenderer != null) {
            this.displayValue = this.column.cellRenderer(params);
        }
        if (this.column.cellType != TableCellType.button &&
            this.column.cellType != TableCellType.color &&
            this.column.cellType != TableCellType.link &&
            this.column.toolTipGetter == null &&
            this.toolTip?.getComponentOnHover() == null) {
            let tooltipText = this.getTooltipText();
            this.params.setTooltip(tooltipText, () => {
                let element = this.value?.nativeElement;
                return element != null && element.scrollWidth > element.clientWidth + 1;
            });
            this.truncationTooltipActive = true;
        }
        else if (this.truncationTooltipActive) {
            this.params.setTooltip("");
            this.truncationTooltipActive = false;
        }
        let wrapper = Arrays.getSafe(this.params.eGridCell?.children, 0);
        if (wrapper != null) {
            //Das benötigen wir damit, z.B. Buttons vertikal zentriert angezeigt werden
            wrapper.classList?.add("height100p");
        }
    }

    refresh(params: ICellRendererParams): boolean {
        this.agInit(params);
        return true;
    }

    getKeepHtml(): boolean {
        return this.column?.cellRenderer != null ||
            this.column?.keepHtml ||
            this.tableCell?.keepHtml;
    }

    async buttonClick(event: any, button: TableCellButton): Promise<void> {
        if (this.column.id == SmTableComponent.COLUMN_ID_REMOVE) {
            event.preventDefault();
            event.stopPropagation();
            await this.parentTable.removeRow(this.tableRow, true);
        }
        else if (this.column.id == SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN) {
            await this.parentTable.editRowFullScreen(this.params.node);
        }
        else {
            if (this.column.cellType == TableCellType.link) {
                event.preventDefault();
                event.stopPropagation();
            }
            this.parentTable.onCellClicked.emit(
                new TableCellEvent(
                    button,
                    this.tableRow,
                    this.params.colDef,
                    0,
                    0,
                ),
            );
        }
    }

    getVisibleButtons(): TableCellButton[] {
        return (this.params.value ?? []).filter((button: TableCellButton) => button?.visible);
    }

    getVisibleAddOnButtons(): TableCellButton[] {
        return (this.tableCell?.addOnButtons ?? []).filter(button => button?.visible);
    }

    async addOnButtonClick(event: MouseEvent, button: TableCellButton): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        await this.buttonClick(event, button);
    }

    getDirectButtons(): TableCellButton[] {
        return this.column.burgerMenu ? [] : this.getVisibleButtons().filter(button => !button.burgerMenu);
    }

    getBurgerMenuButtons(): TableCellButton[] {
        return this.getVisibleButtons().filter(button => this.column.burgerMenu || button.burgerMenu);
    }

    burgerMenuClick(event: any): void {
        let menu: MenuItem[] = this.getBurgerMenuButtons().map(button => ({
            label: button.description ?? button.id,
            icon: button.icon,
            command: () => void this.buttonClick(event, button),
        }));
        this.app.openContextMenu(menu);
        event.preventDefault();
        event.stopPropagation();
    }

    getObjectActionsFromProviders(): ObjectActionWithObject[] {
        return this.parentTable.getCellSemanticObjectActions(this.params);
    }

    getObjectActions(): ObjectActionWithObject[] {
        return this.parentTable.getCellNonSemanticObjectActions(this.params, this.buttonActions);
    }

    async actionButtonClick(event: any, action: ObjectActionWithObject): Promise<void> {
        await action.perform();
        event.preventDefault();
        event.stopPropagation();
    }

    actionContextMenuButtonClick(event: any): void {
        let menu: MenuItem[] = ObjectActionRegistry.getSemanticActionMenuItems(this.buttonActions.filter(a => a.action.isUsable(a.on)));
        this.app.openContextMenu(menu);
        event.preventDefault();
        event.stopPropagation();
    }

    actionContextButtonClick(event: any): void {
        let menu: MenuItem[] = this.getObjectActions()
            .filter(a => a.action.isUsable(a.on))
            .map(a => ({
                label: a.action.getCaption(a.on),
                icon: "fas fa-open",
                command: _ => a.perform(),
            }));
        this.app.openContextMenu(menu);
        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener("mouseenter")
    mouseEnter(): void {
        this.mouseInside = true;
    }

    @HostListener("mouseleave")
    mouseLeave(): void {
        this.mouseInside = false;
    }

    async addRow(): Promise<void> {
        await this.parentTable.addRow();
    }

    private getTooltipText(): string {
        let value = Objects.toString(this.displayValue);
        if (!value.includes("<") && !value.includes("&")) {
            return value;
        }

        let element = document.createElement("div");
        element.innerHTML = value;
        return element.textContent ?? "";
    }

}
